import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { fmtDate, fmtAmt } from './helpers';

const BANK = {
  name: 'SHAHBAZ MUNIR',
  bank: 'Meezan Bank – Peoples Colony, Faisalabad',
  account: '04030100161735',
  iban: 'PK35MEZN0004030100161735',
};

// International clients (billed in USD/GBP/AED) pay into the company's Bank Alfalah
// account, which supports SWIFT wire transfers.
const BANK_INTL = {
  title: 'STRATEGISTIS INFORMATIONS (SMC-PVT) LTD.',
  bank: 'Bank Alfalah Limited',
  branchCode: '5729',
  account: '5001988069',
  iban: 'PK91ALFH5729005001988069',
  swift: 'ALFHPKKA729',
};

export function generateInvoicePDF(client, invoice) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210;
  const mg = 16;

  // Background — obsidian
  doc.setFillColor(10, 10, 12);
  doc.rect(0, 0, W, 297, 'F');

  // Top accent bar — brass hairline
  doc.setFillColor(169, 137, 83);
  doc.rect(0, 0, W, 1, 'F');

  // Header area — graphite
  doc.setFillColor(20, 20, 23);
  doc.rect(0, 1, W, 43, 'F');

  // Company name — serif (times) bone
  doc.setTextColor(239, 235, 228);
  doc.setFont('times', 'normal');
  doc.setFontSize(22);
  doc.text('Strategists', mg, 20);

  // Brass hairline under wordmark
  doc.setFillColor(169, 137, 83);
  doc.rect(mg, 24, 13, 0.4, 'F');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(150, 146, 138);
  doc.text('MARKETING AGENCY', mg, 30);

  // Invoice label
  doc.setFontSize(7.5);
  doc.setTextColor(120, 117, 110);
  doc.text('SERVICES INVOICE', W - mg, 16, { align: 'right' });

  doc.setFont('times', 'normal');
  doc.setFontSize(17);
  doc.setTextColor(169, 137, 83);
  doc.text(invoice.invoiceNumber, W - mg, 26, { align: 'right' });

  // Status pill — STR muted semantics
  const statusColors = {
    paid: [143, 176, 138], pending: [199, 154, 91], sent: [199, 154, 91], generated: [143, 163, 176]
  };
  const sc = statusColors[invoice.status] || [150, 146, 138];
  doc.setDrawColor(...sc);
  doc.setLineWidth(0.3);
  doc.roundedRect(W - mg - 28, 30, 28, 8, 0.6, 0.6, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...sc);
  doc.text((invoice.status || 'draft').toUpperCase(), W - mg - 14, 35.5, { align: 'center' });

  // Meta row — graphite
  doc.setFillColor(23, 23, 26);
  doc.rect(0, 44, W, 20, 'F');
  const metaItems = [
    ['DATE ISSUED', fmtDate(invoice.generatedDate)],
    ['DUE DATE', fmtDate(invoice.dueDate)],
    ['INVOICE NO.', invoice.invoiceNumber],
  ];
  metaItems.forEach(([label, val], i) => {
    const x = mg + i * 60;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(120, 117, 110);
    doc.text(label, x, 51);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(220, 216, 208);
    doc.text(val || '—', x, 58);
  });

  // Bill To / From — hairline outline cards
  doc.setDrawColor(45, 45, 49);
  doc.setLineWidth(0.2);
  doc.roundedRect(mg, 72, 82, 34, 0.6, 0.6, 'S');
  doc.roundedRect(W - mg - 82, 72, 82, 34, 0.6, 0.6, 'S');

  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 117, 110);
  doc.text('BILL TO', mg + 5, 80);
  doc.text('FROM', W - mg - 77, 80);

  doc.setFontSize(12);
  doc.setFont('times', 'normal');
  doc.setTextColor(239, 235, 228);
  doc.text(client.name || '—', mg + 5, 89);
  doc.text('Strategists', W - mg - 77, 89);

  if (client.email) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 146, 138);
    doc.text(client.email, mg + 5, 97);
  }
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(150, 146, 138);
  doc.text('Faisalabad, Pakistan', W - mg - 77, 97);

  // Service table — with optional reconciliation rows
  const curr = invoice.currency || client.currency || 'PKR';
  const serviceDesc = client.billingType === 'commission'
    ? `Marketing services — ${client.commissionRate}% commission\non sales of ${fmtAmt(invoice.salesFigure || 0, curr)}`
    : 'Monthly marketing retainer services';

  const recon = invoice.reconciliation;
  const tableBody = [];

  // Current month line
  tableBody.push([serviceDesc, client.billingType === 'commission' ? 'Commission' : 'Fixed Retainer', fmtAmt(invoice.amount, curr)]);

  // Prior outstanding invoice lines
  if (recon && recon.priorInvoices && recon.priorInvoices.length > 0) {
    recon.priorInvoices.forEach(pi => {
      const mo = pi.monthYear ? pi.monthYear : '';
      tableBody.push([
        `Outstanding — ${pi.invoiceNumber || ''} (${mo})`,
        'Prior balance',
        fmtAmt(pi.amount, curr),
      ]);
    });
  }

  const totalDue = recon ? recon.cumulativeTotal : invoice.amount;

  doc.autoTable({
    startY: 115,
    margin: { left: mg, right: mg },
    head: [['Description', 'Type', `Amount (${curr})`]],
    body: tableBody,
    foot: [[
      { content: recon ? 'CUMULATIVE TOTAL DUE' : 'TOTAL DUE', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold', textColor: [10, 10, 12], fillColor: [169, 137, 83] } },
      { content: fmtAmt(totalDue, curr), styles: { font: 'times', fontStyle: 'bold', textColor: [10, 10, 12], fillColor: [169, 137, 83] } },
    ]],
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 6, textColor: [225, 221, 213], fillColor: [18, 18, 21], lineColor: [45, 45, 49], lineWidth: 0.15 },
    headStyles: { fillColor: [23, 23, 26], textColor: [150, 146, 138], fontStyle: 'bold', fontSize: 7.5, cellPadding: { top: 5, bottom: 5, left: 6, right: 6 } },
    footStyles: { fillColor: [169, 137, 83], fontSize: 10, textColor: [10, 10, 12] },
    columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 38, halign: 'center' }, 2: { cellWidth: 42, halign: 'right' } },
  });

  const y = doc.lastAutoTable.finalY + 8;

  // Payment details — international clients get the SWIFT-enabled Bank Alfalah account
  const rows = curr !== 'PKR'
    ? [
        ['Account Title', BANK_INTL.title],
        ['Bank', BANK_INTL.bank],
        ['Branch Code', BANK_INTL.branchCode],
        ['Account Number', BANK_INTL.account],
        ['IBAN', BANK_INTL.iban],
        ['SWIFT', BANK_INTL.swift],
      ]
    : [
        ['Account Name', BANK.name],
        ['Bank', BANK.bank],
        ['Account Number', BANK.account],
        ['IBAN', BANK.iban],
      ];
  const payH = 16 + rows.length * 7;
  doc.setFillColor(20, 20, 23);
  doc.roundedRect(mg, y, W - mg * 2, payH, 0.6, 0.6, 'F');
  doc.setDrawColor(45, 45, 49);
  doc.setLineWidth(0.2);
  doc.roundedRect(mg, y, W - mg * 2, payH, 0.6, 0.6, 'S');
  // header strip
  doc.setFillColor(23, 23, 26);
  doc.rect(mg, y, W - mg * 2, 8, 'F');
  doc.setDrawColor(45, 45, 49);
  doc.line(mg, y + 8, W - mg, y + 8);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(169, 137, 83);
  doc.text('PAYMENT DETAILS', mg + 6, y + 5.5);

  let py = y + 16;
  rows.forEach(([lbl, val]) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 117, 110);
    doc.text(lbl, mg + 6, py);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(220, 216, 208);
    doc.text(val, mg + 42, py);
    py += 7;
  });

  // Footer
  doc.setFillColor(20, 20, 23);
  doc.rect(0, 278, W, 19, 'F');
  doc.setFillColor(169, 137, 83);
  doc.rect(0, 296, W, 1, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(120, 117, 110);
  doc.text('Thank you for your business.', mg, 289);
  doc.setFont('times', 'normal');
  doc.setTextColor(200, 196, 188);
  doc.text('strategists.pk', W - mg, 289, { align: 'right' });

  // Create a safe filename from the invoice title
  const safeName = (invoice.invoiceNumber || 'Invoice')
    .replace(/\|/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
  doc.save(`${safeName}.pdf`);
}
